// src/pages/NewsArticlePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://mrm.lemda.lk/api';

// Fallback image (inline SVG)
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1400" height="700" viewBox="0 0 1400 700">
    <defs>
      <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stop-color="#e5e7eb"/>
        <stop offset="100%" stop-color="#cbd5e1"/>
      </linearGradient>
    </defs>
    <rect width="1400" height="700" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="700" y="340" font-size="36" font-weight="700">Article</text>
      <text x="700" y="380" font-size="16" opacity="0.85">Cover not available</text>
    </g>
  </svg>`);

// Micro “sanitizer”: strips <script> tags. (Assumes server already sanitizes HTML.)
function stripScripts(html = '') {
  return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
}

function readingTimeFromHtml(html = '') {
  const txt = html.replace(/<[^>]+>/g, ' ');
  const words = txt.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function NewsArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // fetch the article
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await fetch(`${API_BASE}/news.php?slug=${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Article not found');
        if (!ignore) setArticle(json.data);
      } catch (e) {
        if (!ignore) {
          setErr(e.message || 'Failed to load');
          setArticle(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [slug]);

  // fetch “more stories” (simple: pull latest & filter out current slug)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/news.php?status=published&limit=6`);
        if (!res.ok) return;
        const json = await res.json();
        if (!json.ok) return;
        const list = (json.data || []).filter(a => a.slug !== slug).slice(0, 3);
        if (!ignore) setMore(list);
      } catch {}
    })();
    return () => { ignore = true; };
  }, [slug]);

  // SEO title
  useEffect(() => {
    if (article?.title) document.title = `${article.title} — MRM Lettings`;
    return () => { /* no-op */ };
  }, [article]);

  const cover = article?.full_image_url || article?.cover_image_url || PLACEHOLDER;
  const bodyHtml = useMemo(() => stripScripts(article?.body || article?.excerpt || ''), [article]);
  const readTime = useMemo(() => readingTimeFromHtml(article?.body || article?.excerpt || ''), [article]);

  // Share handlers
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: article?.title || 'Article', url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch { /* ignore */ }
  };

  if (!loading && err) {
    return (
      <div style={{ padding: '4rem 1rem', fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '1.8rem' }}>We couldn’t find that article</h1>
          <p style={{ color: '#475569' }}>{err}</p>
          <a href="/news" style={{ color: '#1e3a8a', fontWeight: 800, textDecoration: 'none' }}>Back to news</a>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .article-page { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
        .hero {
          position: relative;
          min-height: 44vh;
          display: grid;
          place-items: end start;
          background: #e5e7eb;
          overflow: hidden;
        }
        .hero img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
        .hero::after {
          content:""; position:absolute; inset:0;
          background: linear-gradient(180deg, rgba(0,0,0,.0) 0%, rgba(0,0,0,.55) 100%);
        }
        .hero-inner { position:relative; z-index:2; width:100%; }
        .container { max-width: 1000px; margin: 0 auto; padding: 0 1rem; }
        .breadcrumbs { color:#e2e8f0; font-weight:700; letter-spacing:.02em; text-transform:uppercase; font-size:.78rem; opacity:.95; }
        .title { color:#ffffff; font-weight:900; line-height:1.05; margin:.4rem 0 1rem; font-size: clamp(1.6rem, 3.6vw, 2.6rem); max-width: 900px; }
        .meta { display:flex; gap:.75rem; flex-wrap:wrap; color:#e2e8f0; opacity:.95; font-size:.92rem; }
        .pill { display:inline-flex; align-items:center; gap:.5rem; background:rgba(255,255,255,.15); border:1px solid rgba(255,255,255,.25); border-radius:999px; padding:.35rem .7rem; }

        .content-wrap { background:#fff; }
        .content { max-width: 900px; margin: 0 auto; padding: clamp(1.25rem, 3vw, 2rem) 1rem; }
        .content .lead { font-size:1.1rem; color:#334155; line-height:1.7; }
        .prose { color:#0f172a; line-height:1.75; font-size:1.05rem; }
        .prose h2 { font-size:1.5rem; margin:1.8rem 0 .6rem; line-height:1.25; }
        .prose h3 { font-size:1.25rem; margin:1.35rem 0 .4rem; }
        .prose p { margin:1rem 0; }
        .prose ul, .prose ol { padding-left: 1.25rem; margin: .75rem 0 1rem; }
        .prose blockquote { border-left: 4px solid #1e3a8a; padding: .25rem .9rem; margin: 1.25rem 0; color:#334155; background:#f8fafc; }
        .prose img { max-width:100%; height:auto; border-radius: 10px; }

        .share { display:flex; gap:.6rem; align-items:center; margin-top: .75rem; }
        .btn-share {
          border:none; cursor:pointer; background:#1e3a8a; color:#fff; font-weight:800; border-radius:999px;
          padding:.6rem .9rem; font-size:.92rem; transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
        }
        .btn-share:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(30,58,138,.25); background:#173074; }
        .backlink { display:inline-block; margin-top: .75rem; color:#1e3a8a; font-weight:800; text-decoration:none; }
        .backlink:hover { color:#173074; }

        .more { background:#f8fafc; padding: clamp(1.5rem, 3vw, 2.5rem) 1rem; }
        .more .container { max-width: 1000px; }
        .more-title { margin:0 0 1rem; color:#0f172a; font-size:1.3rem; font-weight:900; }
        .grid { display:grid; grid-template-columns:1fr; gap:1rem; }
        @media (min-width: 768px){ .grid { grid-template-columns: 1fr 1fr 1fr; } }
        .card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; display:grid; grid-template-rows: 150px auto; }
        .card .thumb { background:#e5e7eb; overflow:hidden; }
        .card .thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .card .body { padding: .9rem; }
        .card .t { margin:0; color:#0f172a; font-weight:800; font-size:1rem; line-height:1.25; }
        .card .d { margin:.25rem 0 0; color:#64748b; font-size:.85rem; }
        .skeleton { background: #fff; border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; }
        @keyframes shimmer { 0%{background-position:-450px 0} 100%{background-position:450px 0} }
        .sk-rect { height: 260px; background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; }
        .sk-line { height:12px; margin: .6rem; border-radius: 6px; background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; }
      `}</style>

      {/* HERO */}
      <header className="article-page hero">
        <img
          src={cover}
          alt={article?.title || 'Article cover'}
          onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
          loading="eager"
        />
        <div className="hero-inner">
          <div className="container" style={{ paddingBottom: '1.25rem' }}>
            <div className="breadcrumbs">
              <a href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</a> &nbsp;/&nbsp;
              <a href="/news" style={{ color: 'inherit', textDecoration: 'none' }}>News</a>
            </div>
            <h1 className="title">{article?.title || 'Loading…'}</h1>
            <div className="meta">
              {article?.published_at && <span className="pill">{fmtDate(article.published_at)}</span>}
              {article?.author && <span className="pill">{article.author}</span>}
              {!loading && <span className="pill">{readTime}</span>}
            </div>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="content-wrap">
        <div className="content">
          {/* Loading skeleton */}
          {loading && (
            <div className="skeleton">
              <div className="sk-rect" />
              <div className="sk-line" style={{ width: '70%' }} />
              <div className="sk-line" style={{ width: '90%' }} />
              <div className="sk-line" style={{ width: '60%' }} />
            </div>
          )}

          {!loading && (
            <>
              {article?.excerpt && <p className="lead">{article.excerpt}</p>}

              {/* Article body (expects sanitized HTML from server). We strip <script> just in case. */}
              <article
                className="prose"
                dangerouslySetInnerHTML={{ __html: bodyHtml || '' }}
              />

              <div className="share">
                <button className="btn-share" type="button" onClick={handleShare}>Share</button>
                <a className="backlink" href="/news">← Back to news</a>
              </div>
            </>
          )}
        </div>
      </main>

      {/* MORE STORIES */}
      <aside className="more">
        <div className="container">
          <h3 className="more-title">More stories</h3>
          <div className="grid">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="skeleton">
                    <div className="sk-rect" style={{ height: 150 }} />
                    <div className="sk-line" style={{ width: '80%' }} />
                    <div className="sk-line" style={{ width: '40%' }} />
                  </div>
                ))
              : more.map((m) => {
                  const mCover = m.full_image_url || m.cover_image_url || PLACEHOLDER;
                  const href = m.href || `/news/${m.slug}`;
                  return (
                    <a key={m.id} className="card" href={href}>
                      <div className="thumb">
                        <img
                          src={mCover}
                          alt={m.title}
                          onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                          loading="lazy"
                        />
                      </div>
                      <div className="body">
                        <h4 className="t">{m.title}</h4>
                        {m.published_at && <div className="d">{fmtDate(m.published_at)}</div>}
                      </div>
                    </a>
                  );
                })}
          </div>
        </div>
      </aside>

      {/* JSON-LD Article schema for SEO */}
      {!loading && article?.title && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: article.excerpt || undefined,
            datePublished: article.published_at || undefined,
            author: article.author ? { '@type': 'Person', name: article.author } : undefined,
            image: cover,
            mainEntityOfPage: typeof window !== 'undefined' ? window.location.href : undefined,
            publisher: {
              '@type': 'Organization',
              name: 'MRM Lettings Property & Management Ltd',
              logo: { '@type': 'ImageObject', url: cover }
            }
          })
        }} />
      )}
    </>
  );
}
